import { CaregiverDto, SiteAddressDto } from '@ecdlink/core';
import { CaregiverInput, SiteAddressInput } from '@ecdlink/graphql';
import {
  createAction,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import { CaregiverService } from '@services/CaregiverService';
import { RootState, ThunkApiType } from '../types';
import { CaregiverContactHistory, MergedCaregiver } from './caregiver.types';

export const CaregiverActions = {
  GET_CAREGIVERS_HEALTH_CARE_WORKER: 'getCaregiversHealthCareWorker',
  UPDATE_CONTACT_HISTORY: 'updateContactCaregiverHistory',
  ADD_CONTACT_HISTORY: 'addContactCaregiverHistory',
  GET_CAREGIVER_CLIENTS: 'getCaregiverClients',
  GET_ALL_CAREGIVER_CLIENTS: 'getAllCaregiverClients',
};

export const getCaregiversForHealthCareWorker = createAsyncThunk<
  CaregiverDto[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  CaregiverActions.GET_CAREGIVERS_HEALTH_CARE_WORKER,
  async (_, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      caregivers: { caregivers: caregiversCache },
      user: { user: userCache },
    } = getState();

    const cacheBelongsToAuthUser =
      !!userAuth?.id && (!userCache?.id || userCache.id === userAuth.id);

    // Return cached data immediately if present and for this user
    if (
      cacheBelongsToAuthUser &&
      caregiversCache &&
      caregiversCache.length > 0
    ) {
      return caregiversCache;
    }

    // Ensure we have a token
    if (!userAuth?.auth_token || !userAuth?.id) {
      return rejectWithValue(
        'no access token or user id, authentication required'
      );
    }

    try {
      const caregivers: CaregiverDto[] = await new CaregiverService(
        userAuth?.auth_token
      ).getCaregiversForHealthCareWorker();

      if (!caregivers) {
        return rejectWithValue('Error getting caregivers');
      }

      return caregivers;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateCaregiverContactHistory = createAction<
  PayloadAction<CaregiverContactHistory[]>
>(CaregiverActions.UPDATE_CONTACT_HISTORY);

export const addCaregiverContactHistory = createAction<
  PayloadAction<CaregiverContactHistory>
>(CaregiverActions.ADD_CONTACT_HISTORY);

export const upsertCareGivers = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>('upsertCareGivers', async (_, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    caregivers: { caregivers },
  } = getState();

  try {
    if (userAuth?.auth_token && caregivers) {
      for (const caregiver of caregivers) {
        const input = mapCaregiver(caregiver);
        input.HealthCareWorkerId = userAuth.id;

        if (caregiver.siteAddress) {
          const addressInput = mapSiteAddress(caregiver.siteAddress);
          input.SiteAddressId = addressInput.Id;
        }
        if ('synced' in caregiver && caregiver.synced === false) {
          await new CaregiverService(userAuth?.auth_token).updateCareGiver(
            caregiver.id ?? '',
            input
          );
        }
      }
    }
    return [true];
  } catch (err) {
    return rejectWithValue(err);
  }
});

type CreateCaregiverRequest = {
  caregiver: CaregiverDto;
};

export const createCaregiver = createAsyncThunk<
  CaregiverDto,
  CreateCaregiverRequest,
  ThunkApiType<RootState>
>('createCaregiver', async ({ caregiver }, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
  } = getState();
  try {
    let mappedCaregiverInput = mapCaregiver(caregiver);

    if (userAuth?.auth_token) {
      return await new CaregiverService(userAuth?.auth_token).createCaregiver(
        mappedCaregiverInput
      );
    } else {
      return rejectWithValue('no access token, profile check required');
    }
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const getCaregiverClients = createAsyncThunk<
  MergedCaregiver,
  { caregiverId: string },
  ThunkApiType<RootState>
>(
  CaregiverActions.GET_CAREGIVER_CLIENTS, // should be e.g. 'caregiver/getClients'
  async ({ caregiverId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      caregivers: { caregiverClientsList },
    } = getState();

    // 1. Better cache check – only return if clients are actually loaded
    const cached = caregiverClientsList?.find((c) => c.id === caregiverId);
    if (cached) {
      // or cached?.clientsLoaded, or !cached.isPartial, etc.
      return cached as MergedCaregiver;
    }

    // 2. Auth guard
    if (!userAuth?.auth_token || !userAuth?.id) {
      return rejectWithValue(
        'no access token or user id, authentication required'
      );
    }

    try {
      const service = new CaregiverService(userAuth.auth_token);
      const response = await service.getCaregiverClients(caregiverId);

      // Optional: validate shape (defensive programming)
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from server');
      }

      // Return merged object with guaranteed id
      return {
        id: caregiverId,
        ...response,
      } as MergedCaregiver;
    } catch (err: any) {
      // Richer error payload – very useful in UI
      const errorPayload = {
        message: err.message || 'Failed to fetch caregiver clients',
        status: err.response?.status, // if axios / fetch-response
        code: err.code || 'UNKNOWN_ERROR',
        caregiverId,
      };

      return rejectWithValue(errorPayload);
    }
  }
);

export const getAllCaregiverClients = createAsyncThunk<
  MergedCaregiver[],
  { recordsPerPage?: number; pageNumber?: number },
  ThunkApiType<RootState>
>(
  CaregiverActions.GET_ALL_CAREGIVER_CLIENTS,
  async ({ pageNumber, recordsPerPage }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      caregivers: { caregiverClientsList: caregiversCache },
      user: { user: userCache },
    } = getState();

    // Only reuse offline clients when they belong to the authenticated user
    const cacheBelongsToAuthUser =
      !!userAuth?.id && (!userCache?.id || userCache.id === userAuth.id);

    if (
      cacheBelongsToAuthUser &&
      caregiversCache &&
      caregiversCache.length > 0
    ) {
      return caregiversCache;
    }

    // Ensure we have a token
    if (!userAuth?.auth_token || !userAuth?.id) {
      return rejectWithValue(
        'no access token or user id, authentication required'
      );
    }

    try {
      const caregivers = await new CaregiverService(
        userAuth?.auth_token
      ).getAllCaregiverClients(recordsPerPage, pageNumber);
      return caregivers;
    } catch (err: any) {
      console.error('Failed to fetch getAllCaregiverClients:', err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch getAllCaregiverClients'
      );
    }
  }
);

export type UpdateCaregiverRequest = {
  id: string;
  caregiver: CaregiverDto;
};

export const updateCaregiver = createAsyncThunk<
  CaregiverDto,
  UpdateCaregiverRequest,
  ThunkApiType<RootState>
>(
  'updateCaregiver',
  async ({ caregiver, id }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let mappedCaregiverInput = mapCaregiver(caregiver);

      if (userAuth?.auth_token) {
        return await new CaregiverService(userAuth?.auth_token).updateCareGiver(
          id,
          mappedCaregiverInput
        );
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const mapCaregiver = (x: Partial<CaregiverDto>): CaregiverInput => ({
  FirstName: x.firstName,
  Surname: x.surname,
  Age: x.age,
  PhoneNumber: x.phoneNumber,
  WhatsAppNumber: x.whatsAppNumber,
  RelationId: x.relationId,
  IsActive: true,
  Contribution: true,
  JoinReferencePanel: true,
  isMother: x.isMother === false ? false : true,
  SiteAddress: x.siteAddress && mapSiteAddress(x.siteAddress),
  SiteAddressId: x.siteAddressId,
  IsAllowedCustody: false,
});

const mapSiteAddress = (x: Partial<SiteAddressDto>): SiteAddressInput => ({
  Id: x.id,
  AddressLine1: x.addressLine1,
  AddressLine2: x.addressLine2,
  AddressLine3: x.addressLine3,
  Name: x.name,
  PostalCode: x.postalCode,
  ProvinceId: x.provinceId,
  Ward: x.ward,
  IsActive: x.isActive === false ? false : true,
});
